import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions.ts';
import type { UpdateSessionDto } from '@/types/dtos/sessions/request/update-session.dto.ts';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { invalidatePublicationStateQueries } from '@/state/queries/events/use-publication-state';
import { invalidateSimulationCueSheet } from '@/state/queries/player/use-get-simulation-cue-sheet';

export const useUpdateSessionMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updateSessionDto: UpdateSessionDto) => {
      const token = await getToken();
      return updateSession(updateSessionDto, token ?? '');
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_SESSIONS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_FLOW_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_FLOWS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_SESSION_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY],
        }),
        invalidatePublicationStateQueries(queryClient),
        invalidateSimulationCueSheet(queryClient),
      ]);
    },
  });
};

const updateSession = async (updateSessionDto: UpdateSessionDto, token: string): Promise<SessionDto> => {
  const { id, ...session } = updateSessionDto;
  const url = `${import.meta.env.VITE_API_URL}/sessions/${id}`;
  return sendJSONRequest({ url, method: METHOD.PATCH, body: session, token });
};

export default useUpdateSessionMutation;
